"use client";

import { useEffect } from "react";

/**
 * The only JavaScript in the page grain system: a gate on when the texture is
 * allowed to appear. Nothing here renders the grain — see globals.css for all
 * three layers, and documentation/grain.md for the design.
 *
 * It preloads /images/grain.webp and adds `body.grain-ready` once the tile has
 * fully decoded. That class is what attaches the texture as body's
 * `background-image`. The gate exists because a progressively-downloading
 * background image paints in visible top-to-bottom bands; with it the texture
 * is either absent or complete, never partial.
 *
 * The reveal itself runs in reverse: a canvas background can't animate its
 * opacity, so `body::before` is a page-colored mask over the grain that fades
 * OUT across 400ms when this class lands. Pre-ready that mask is invisible
 * (background color over the same background color), so first paint looks
 * exactly as it would with no grain system at all.
 *
 * `markReady` also fires on `error`, so a failed download can never leave the
 * site permanently textureless.
 *
 * KNOWN LIMITATION — the fade plays even on a warm cache. The tile is served
 * immutable (`public, max-age=31536000, immutable`, next.config.ts) and is
 * `<link rel="preload">`ed in the layout head, so `img.complete` is already
 * true on a repeat visit and this effect adds the class on its first tick. But
 * effects run after hydration, and hydration runs after the document's first
 * paint — which already contains the mask. So there is no way from here to
 * reveal the grain before the user sees one frame of masked page. Landing the
 * class genuinely pre-paint would need an inline <script> in the layout head,
 * which we are deliberately NOT adding: it would be a third inline script for
 * a future CSP to nonce (documentation/security-headers.md), for one frame.
 * That is also why this is a plain `useEffect` — `useLayoutEffect` cannot beat
 * first paint either, and would need an SSR-warning workaround to earn nothing.
 */
const GRAIN_URL = "/images/grain.webp";

const GrainLoader = () => {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.body.classList.contains("grain-ready")) return;

    const markReady = () => {
      document.body.classList.add("grain-ready");
    };

    const img = new Image();
    img.decoding = "async";
    img.src = GRAIN_URL;

    if (img.complete && img.naturalWidth > 0) {
      markReady();
      return;
    }

    img.addEventListener("load", markReady, { once: true });
    // If the image errors for any reason, still reveal so we never leave the
    // site in a "no texture, forever waiting" state.
    img.addEventListener("error", markReady, { once: true });

    return () => {
      img.removeEventListener("load", markReady);
      img.removeEventListener("error", markReady);
    };
  }, []);

  return null;
};

export default GrainLoader;
